<template>
  <v-container fluid class="fill-height" style="min-height: 80vh;">
    <v-row align="center" justify="center">
      <v-col cols="12" sm="10" md="8" lg="6">
        <v-card class="elevation-4 rounded-xl">
          <!-- Header Section -->
          <v-card-title class="d-flex align-center py-6 px-8" style="background-color: #f8f9fa;">
            <v-icon large color="#4285F4" class="mr-3">mdi-calendar-check</v-icon>
            <h2 class="text-h5 font-weight-bold mb-0" style="color: #202124;">การเชื่อมต่อ Google Calendar</h2>
            <v-spacer></v-spacer>
            <v-chip
              v-if="isConnected"
              color="success"
              text-color="white"
              class="font-weight-medium"
            >
              <v-icon left size="18">mdi-check-circle</v-icon>
              เชื่อมต่อแล้ว
            </v-chip>
            <v-chip
              v-else
              color="grey darken-1"
              text-color="white"
              class="font-weight-medium"
            >
              <v-icon left size="18">mdi-alert-circle</v-icon>
              ยังไม่ได้เชื่อมต่อ
            </v-chip>
          </v-card-title>

          <v-divider></v-divider>

          <!-- Content Section -->
          <v-card-text class="px-8 py-8 text-center" v-if="isCheckingPermission">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <p class="mt-4" style="color: #5f6368;">กำลังตรวจสอบสิทธิ์การใช้งาน...</p>
          </v-card-text>

          <v-card-text class="px-8 py-8 text-center" v-else-if="!hasPermission">
            <v-avatar size="100" color="red lighten-5" class="mb-6">
              <v-icon size="50" color="red">mdi-lock-outline</v-icon>
            </v-avatar>
            <h3 class="text-h6 font-weight-medium mb-3" style="color: #202124;">ไม่มีสิทธิ์เข้าถึงฟีเจอร์นี้</h3>
            <p class="text-body-1 mb-0" style="color: #5f6368;">
              ร้านค้าของคุณยังไม่ได้เปิดใช้งานการเชื่อมต่อ Google Calendar ในระบบ
            </p>
          </v-card-text>

          <v-card-text class="px-8 py-8 text-center" v-else>

            <div v-if="!isConnected">
              <v-avatar size="100" color="blue lighten-5" class="mb-6">
                <v-icon size="50" color="blue">mdi-google</v-icon>
              </v-avatar>

              <h3 class="text-h6 font-weight-medium mb-3" style="color: #202124;">ซิงค์ตารางงานของคุณเข้ากับ Google Calendar</h3>
              <p class="text-body-1 mb-8" style="color: #5f6368;">
                เพิ่มความสะดวกในการจัดการคิวงานต่างๆ โดยให้ระบบส่งข้อมูลนัดหมายไปยัง Google Calendar ของคุณโดยอัตโนมัติ
              </p>

              <v-btn
                large
                color="white"
                class="font-weight-bold px-6 py-6 rounded-lg elevation-2"
                style="color: #444; border: 1px solid #dadce0;"
                @click="connectGoogleCalendar"
                :loading="isLoading"
              >
                <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" class="mr-3" style="width: 24px; height: 24px;">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                  <path fill="none" d="M0 0h48v48H0z"></path>
                </svg>
                เชื่อมต่อด้วยบัญชี Google
              </v-btn>
            </div>

            <div v-else>
              <v-avatar size="100" color="green lighten-5" class="mb-6">
                <v-icon size="50" color="success">mdi-check-decagram</v-icon>
              </v-avatar>

              <h3 class="text-h6 font-weight-medium mb-3" style="color: #202124;">เชื่อมต่อบัญชีเรียบร้อยแล้ว</h3>
              <p class="text-body-1 mb-8" style="color: #5f6368;">
                ระบบกำลังซิงค์ข้อมูลนัดหมายเข้ากับบัญชีของคุณ<br>
                <strong class="text-body-1 mt-2 d-inline-block">{{ connectedEmail }}</strong>
              </p>

              <!-- TODO: Add Disconnect API -->
              <v-btn
                text
                color="error"
                class="font-weight-bold px-6 rounded-lg"
                @click="disconnectGoogleCalendar"
                :loading="isLoading"
              >
                <v-icon left>mdi-link-variant-off</v-icon>
                ยกเลิกการเชื่อมต่อ
              </v-btn>
            </div>

          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios'

export default {
  name: 'GoogleCalendarConfig',
  data () {
    return {
      isConnected: false,
      connectedEmail: '',
      isLoading: false,
      popupWindow: null,
      shopId: this.$session.getAll().data.shopId,
      hasPermission: false,
      isCheckingPermission: true
    }
  },
  async mounted () {
    // ตรวจสอบสิทธิ์การใช้งานก่อน
    await this.checkShopPermission()

    if (this.hasPermission) {
      // 1. ดึงข้อมูลสถานะเมื่อโหลดหน้าครั้งแรก
      this.checkStatus()
    }

    console.log('this.$session.getAll().data', this.$session.getAll().data)
    // 2. รับ event message จาก popup window กลับมาบอกว่า login เสร็จแล้ว
    window.addEventListener('message', this.handlePopupMessage)
  },
  beforeDestroy () {
    window.removeEventListener('message', this.handlePopupMessage)
  },
  methods: {
    async checkShopPermission () {
      this.isCheckingPermission = true
      try {
        const response = await axios.get(`${this.$DNS_IP}/sys_shop/get?shopId=${this.shopId}`)
        if (response.data && response.data.length > 0) {
          const shopData = response.data[0]
          if (shopData.statusGoogleCalendar === 'True') {
            this.hasPermission = true
          } else {
            this.hasPermission = false
          }
        } else {
          this.hasPermission = false
        }
      } catch (error) {
        console.error('Failed to check shop permission:', error)
        this.hasPermission = false
      } finally {
        this.isCheckingPermission = false
      }
    },

    connectGoogleCalendar () {
      this.isLoading = true

      const authApiUrl = `${this.$DNS_IP}/GoogleCalendarApi/auth?shopId=${this.shopId}`

      // เปิด Popup
      const width = 500
      const height = 650
      const left = window.screenX + (window.outerWidth - width) / 2
      const top = window.screenY + (window.outerHeight - height) / 2.5

      this.popupWindow = window.open(
        authApiUrl,
        'GoogleCalendarAuth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=0,scrollbars=1,status=1,resizable=1,location=1,menuBar=0`
      )

      // ตรวจสอบว่าผู้ใช้ปิด Popup ไปเองหรือไม่
      const checkPopupClosed = setInterval(() => {
        if (!this.popupWindow || this.popupWindow.closed) {
          clearInterval(checkPopupClosed)
          this.isLoading = false
        }
      }, 1000)
    },

    handlePopupMessage (event) {
      const d = event.data
      if (!d || d.source !== 'gcal-oauth') return

      if (d.success) {
        if (this.popupWindow) {
          this.popupWindow.close()
        }

        // เมื่อเชื่อมต่อสำเร็จ ให้ดึงข้อมูลสถานะล่าสุดมาอัปเดตหน้าจอ
        this.connectedEmail = d.accountEmail
        this.checkStatus()
        this.isLoading = false

        if (this.$swal) {
          this.$swal('สำเร็จ', 'เชื่อมต่อ Google Calendar เรียบร้อยแล้ว', 'success')
        }
      } else {
        this.isLoading = false
        if (this.$swal) {
          this.$swal('ข้อผิดพลาด', 'เชื่อมต่อไม่สำเร็จ: ' + d.message, 'error')
        } else {
          alert('เชื่อมต่อไม่สำเร็จ: ' + d.message)
        }
      }
    },

    async checkStatus () {
      this.isLoading = true
      try {
        const response = await axios.get(`${this.$DNS_IP}/GoogleCalendarApi/status?shopId=${this.shopId}`)
        const data = response.data

        if (data.status && data.connected) {
          this.isConnected = true
          this.connectedEmail = data.accountEmail
        } else {
          this.isConnected = false
          this.connectedEmail = ''
        }
      } catch (error) {
        console.error('Failed to check Google Calendar status:', error)
      } finally {
        this.isLoading = false
      }
    },

    async disconnectGoogleCalendar () {
      this.isLoading = true
      try {
        const response = await axios.post(`${this.$DNS_IP}/GoogleCalendarApi/disconnect`, {
          shopId: this.shopId,
          empId: null
        })

        if (response.data.status) {
          this.isConnected = false
          this.connectedEmail = ''
          if (this.$swal) {
            this.$swal('สำเร็จ', 'ยกเลิกการเชื่อมต่อเรียบร้อยแล้ว', 'success')
          }
        } else {
          if (this.$swal) {
            this.$swal('ข้อผิดพลาด', response.data.message || 'ยกเลิกการเชื่อมต่อไม่สำเร็จ', 'error')
          }
        }
      } catch (error) {
        console.error('Failed to disconnect Google Calendar:', error)
        if (this.$swal) {
          this.$swal('ข้อผิดพลาด', 'เกิดข้อผิดพลาดในการยกเลิกการเชื่อมต่อ', 'error')
        }
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style scoped>
/* ดีไซน์เพิ่มเติมเล็กน้อย เพื่อความสวยงาม */
.v-btn {
  text-transform: none !important;
  letter-spacing: normal !important;
}
</style>



// WEBPACK FOOTER //
// src/components/System/GoogleCalendarConfig.vue